// Fachsprache und Aufrufe der Wiedervorlage (ST-ACT-001, zweiter Schnitt;
// ADR-0052).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie interactions.ts.
//
// DAS WORT "AUFGABE" KOMMT AUF DEM BILDSCHIRM NICHT VOR, und "task", "queue",
// "status" oder "lock" erst recht nicht (NFR-UX-003, CM-UX-010; 5.2.13). Auf
// dem Bildschirm heisst es "Wiedervorlage", "fällig am", "erledigt",
// "nicht mehr nötig". Eine Probe (tasks.test.ts) haelt jeden Text dieser Datei
// gegen dieselbe Verbotsliste wie die Notiz.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';
import { PRODUKT_ZEITZONE } from './parties.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Die Grenze des Titels -- dieselbe Zahl wie MAXIMUM_TASK_TITLE_LENGTH in
 * apps/api/src/domain/engagement/task-commands.ts. Die Probe liest sie dort und
 * schlaegt an, wenn beide auseinanderlaufen.
 */
export const MAXIMUM_TITLE_LENGTH = 200;

/** Die Vorbelegung des Titels (Entscheidung 9) -- dieselbe wie im Server. */
export const DEFAULT_TITLE = 'Wiedervorlage';

export interface TaskView {
  readonly taskId: string;
  readonly title: string;
  /** Kalendertag als YYYY-MM-DD; die Zeitzone hat der Server schon gerechnet. */
  readonly dueOn: string;
  readonly overdue: boolean;
  readonly status: string;
  readonly lockVersion: number;
  readonly partyId: string;
  readonly partyDisplayName: string;
  readonly interactionId: string;
  readonly createdAt: string;
}

export type TasksResult =
  | { readonly outcome: 'loaded'; readonly tasks: readonly TaskView[]; readonly hasMore: boolean; readonly nextCursor?: string }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type TaskCommandResult =
  | { readonly outcome: 'applied'; readonly taskId: string; readonly dueOn: string; readonly lockVersion: number; readonly status: string }
  | { readonly outcome: 'gone' }
  | { readonly outcome: 'field-rejected'; readonly message: string }
  // `reasonCode`: der Grund des Servers, wo er einen nannte -- der Kontakt
  // liest daran ab, ob er sich neu laden muss (409 party_not_writable).
  | { readonly outcome: 'rejected'; readonly message: PlainMessage; readonly reasonCode?: string };

/**
 * Die drei Lagen, die diese Oberflaeche selbst erklaeren muss, weil der
 * allgemeine Grundcode-Text sie nicht trifft. Jede nennt Problem UND naechsten
 * Schritt (NFR-UX-005: keine Sackgasse).
 */
export const NOT_OWNED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Wiedervorlage gehört einer anderen Person.',
  explanation: 'Du siehst sie, weil ihr denselben Kontakt betreut. Ändern kann sie nur, wer sie sich gesetzt hat.',
  nextStep: 'Sprich die Kollegin oder den Kollegen an, oder setz dir deine eigene Wiedervorlage an dieser Notiz.',
  retryable: false,
});

export const ALREADY_OPEN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Du hast dir an dieser Notiz schon etwas vorgemerkt.',
  explanation: 'Zu einer Notiz gibt es eine offene Wiedervorlage je Person — so bleibt klar, was als Nächstes ansteht.',
  nextStep: 'Verschieb den Termin der vorhandenen, oder hak sie ab und setz eine neue.',
  retryable: false,
});

export const NOT_OPEN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Wiedervorlage ist schon erledigt.',
  explanation: 'Jemand — vielleicht du in einem anderen Fenster — hat sie abgehakt oder zurückgezogen.',
  nextStep: 'Lade die Seite neu. Wenn noch etwas offen ist, setz dir eine neue Wiedervorlage.',
  retryable: true,
});

export const OUTDATED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Wiedervorlage hat sich inzwischen geändert.',
  explanation: 'Sie wurde bearbeitet, während du sie offen hattest. LIAISON überschreibt die neuere Fassung nicht.',
  nextStep: 'Lade die Seite neu und schau dir den aktuellen Stand an.',
  retryable: true,
});

export const NOT_COMPLETED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Wiedervorlage ist nicht mehr erledigt.',
  explanation: 'Sie wurde inzwischen wieder geöffnet oder zurückgezogen, vielleicht in einem anderen Fenster.',
  nextStep: 'Die Liste zeigt jetzt den aktuellen Stand.',
  retryable: false,
});

const REASON_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  task_not_owned: NOT_OWNED_MESSAGE,
  task_already_open: ALREADY_OPEN_MESSAGE,
  task_not_open: NOT_OPEN_MESSAGE,
  task_not_completed: NOT_COMPLETED_MESSAGE,
  task_outdated: OUTDATED_MESSAGE,
});

// "Nicht beschreibbar" gilt laut WRITABLE_LIFECYCLE_STATUSES (write-core/lifecycle.ts)
// fuer archivierte, zusammengefuehrte und anonymisierte Kontakte; aendert sich die
// Liste dort, aendert sich dieser Satz mit. Der naechste Schritt haengt vom Befehl ab,
// deshalb hat jeder Befehl seine eigene Tabelle, die vor REASON_MESSAGES gilt.
const KONTAKT_NICHT_BESCHREIBBAR = 'Der Kontakt wurde inzwischen archiviert, zusammengeführt oder anonymisiert.';

const REOPEN_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  party_not_writable: Object.freeze({
    headline: 'Diese Wiedervorlage lässt sich nicht wieder öffnen.',
    explanation: `${KONTAKT_NICHT_BESCHREIBBAR} An ihm lässt sich nichts mehr vormerken.`,
    nextStep: 'Sie bleibt erledigt. Die Seite zeigt jetzt den aktuellen Stand.',
    retryable: false,
  }),
  task_already_open: Object.freeze({
    headline: 'Du hast an dieser Notiz schon eine offene Wiedervorlage.',
    explanation: 'Je Notiz gibt es eine offene Wiedervorlage je Person. Die erledigte bleibt deshalb erledigt.',
    nextStep: 'Die offene steht in deiner Liste. Passt ihr Termin nicht, verschieb ihn im Verlauf des Kontakts.',
    retryable: false,
  }),
});

const DUE_CHANGE_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  party_not_writable: Object.freeze({
    headline: 'Der Termin lässt sich nicht mehr verschieben.',
    explanation: `${KONTAKT_NICHT_BESCHREIBBAR} Neue Termine lassen sich an ihm nicht mehr vormerken.`,
    nextStep: 'Hak die Wiedervorlage als erledigt ab oder zieh sie zurück; das geht weiterhin.',
    retryable: false,
  }),
});

const CREATE_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  party_not_writable: Object.freeze({
    headline: 'Hier lässt sich keine Wiedervorlage mehr setzen.',
    explanation: KONTAKT_NICHT_BESCHREIBBAR,
    nextStep: 'Die Seite zeigt jetzt den aktuellen Stand.',
    retryable: false,
  }),
});

/** Die Feldregel des Kalendertags -- die Oberflaeche ist die freundlichere Kontrolle, nie die einzige. */
export function dueOnFieldProblem(raw: string, heute: string): string | undefined {
  if (raw.trim().length === 0) {
    return 'Für welchen Tag möchtest du dir das vormerken?';
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return 'Gib den Tag als Datum an.';
  }
  if (raw < heute) {
    return 'Wähl heute oder einen späteren Tag.';
  }
  return undefined;
}

/** Hoechstens so viele Zeichen traegt die Vorbelegung aus der Notiz (D-15). */
export const ANLASS_LAENGE = 60;

/**
 * Der Anlass einer Wiedervorlage aus ihrer Notiz: der Notizanfang, damit die
 * Liste sagt, worum es geht, statt "Wiedervorlage" (D-15). Eine Zeile --
 * Umbrueche und Leerraum werden ein Leerzeichen --, hoechstens ANLASS_LAENGE
 * Zeichen, gekuerzt an einer Wortgrenze mit "…". Die Vorbelegung steht sichtbar
 * im Feld und ist aenderbar; der Titel ist so sichtbar wie die Notiz selbst
 * (ADR-0052 Ziffer 3) und steht nie im Audit (Ziffer 4).
 */
export function anlassAusNotiz(notiz: string): string {
  const zeile = notiz.replace(/\s+/gu, ' ').trim();
  const zeichen = Array.from(zeile);
  if (zeichen.length <= ANLASS_LAENGE) return zeile;
  const rest = zeichen.slice(0, ANLASS_LAENGE);
  let luecke = rest.length - 1;
  while (luecke > 0 && rest[luecke] !== ' ') luecke -= 1;
  const ende = luecke > 0 ? luecke : ANLASS_LAENGE - 1;
  return `${zeichen.slice(0, ende).join('').trimEnd()}…`;
}

/** Die Feldregel des Titels. Leer ist erlaubt -- dann trägt LIAISON die Vorbelegung ein. */
export function titleFieldProblem(raw: string): string | undefined {
  let laenge = 0;
  for (const _ of raw.trim()) laenge += 1;
  return laenge > MAXIMUM_TITLE_LENGTH ? 'Kürze den Text auf höchstens zweihundert Zeichen.' : undefined;
}

/**
 * Der Kalendertag in Worten. KEINE Rechnerei im Browser ueber "überfällig" --
 * das sagt der Server (er kennt die Zeitzone; ADR-0052 Ziffer 5). Hier wird nur
 * geschrieben, was er gesagt hat.
 */
export function dueLabel(dueOn: string, overdue: boolean): string {
  const [jahr, monat, tag] = dueOn.split('-');
  const datum = jahr === undefined || monat === undefined || tag === undefined ? dueOn : `${tag}.${monat}.${jahr}`;
  // Der Zustand steht in WORTEN und nicht allein in einer Farbe (NFR-ACC-001).
  return overdue ? `überfällig seit ${datum}` : `fällig am ${datum}`;
}

/** Länge des Titels im Knopfnamen; darüber wird an der Wortgrenze gekürzt. */
const KNOPFNAME_TITELLAENGE = 60;

/**
 * Der Name eines Knopfes an der Wiedervorlage eines Verlaufseintrags (WCAG
 * 2.4.6): "<Wort>: <Titel>", damit gleichnamige Knöpfe mehrerer Einträge
 * unterscheidbar sind. Ein langer Titel endet an der Wortgrenze mit "…" (ohne
 * Leerzeichen davor). Ein leerer Titel und die Vorbelegung (DEFAULT_TITLE) sagen
 * nichts über den Eintrag; dann nennt der Name Art und Zeitpunkt der Notiz
 * (`notizBezug`, z. B. "Notiz vom 10.09.2026, 16:00").
 */
export function followUpButtonName(wort: string, titel: string, notizBezug: string): string {
  const zeichen = Array.from(titel.trim());
  const text = zeichen.join('');
  if (zeichen.length === 0 || text === DEFAULT_TITLE) {
    return `${wort}: Wiedervorlage, ${notizBezug}`;
  }
  if (zeichen.length <= KNOPFNAME_TITELLAENGE) {
    return `${wort}: ${text}`;
  }
  // Steht an der Grenze ein Leerzeichen, ist das Wort davor vollständig.
  const rest = zeichen.slice(0, KNOPFNAME_TITELLAENGE + 1);
  let letzteLuecke = rest.length - 1;
  while (letzteLuecke > 0 && !/\s/.test(rest[letzteLuecke]!)) letzteLuecke -= 1;
  const ende = letzteLuecke > 0 ? letzteLuecke : KNOPFNAME_TITELLAENGE;
  return `${wort}: ${zeichen.slice(0, ende).join('').trimEnd()}…`;
}

/** Die Zusammenfassung der Liste -- eine Zahl in Worten, kein Zaehlerstand. */
export function taskSummary(count: number, hasMore: boolean): string {
  if (count === 0) {
    return 'Du hast gerade nichts vorgemerkt.';
  }
  const gezaehlt = count === 1 ? 'Eine Wiedervorlage' : `${count} Wiedervorlagen`;
  // "weitere", nicht "ältere": die Liste kommt nach Fälligkeit, was nachkommt, ist später fällig.
  return hasMore ? `${gezaehlt} — es gibt noch weitere.` : gezaehlt;
}

/** Der Zählsatz im Seitenkopf der Wiedervorlagen (v4): Zahl und Ordnung, nicht mehr. */
export function openSummary(count: number, hasMore: boolean): string {
  return `${hasMore ? 'mindestens ' : ''}${count} offen, das Fälligste zuerst`;
}

/**
 * Der Name eines Knopfes in der Wiedervorlagen-Liste: wie am Verlauf
 * (followUpButtonName), und IMMER mit dem Kontakt -- anders als im Verlauf
 * steht hier keine Beschreibung daneben, und zwei Kontakte koennen gleiche
 * Titel tragen (WCAG 2.4.6).
 */
export function taskButtonName(wort: string, titel: string, kontakt: string): string {
  const bezug = `bei ${kontakt}`;
  // Leerer Titel und Vorbelegung: followUpButtonName setzt den Bezug selbst ein.
  const ohneEigenenTitel = titel.trim().length === 0 || titel.trim() === DEFAULT_TITLE;
  const name = followUpButtonName(wort, titel, bezug);
  return ohneEigenenTitel ? name : `${name} ${bezug}`;
}

export type FaelligkeitsGruppe = 'ueberfaellig' | 'heute' | 'diese-woche' | 'spaeter';

export const FAELLIGKEITS_GRUPPEN: Readonly<Record<FaelligkeitsGruppe, string>> = {
  ueberfaellig: 'Überfällig',
  heute: 'Heute',
  'diese-woche': 'Diese Woche',
  spaeter: 'Später',
};

/**
 * Die Gruppen der Wiedervorlagen-Liste in fester Reihenfolge; leere fallen weg,
 * die Reihenfolge innerhalb einer Gruppe bleibt die des Servers.
 *
 * "Überfällig" sagt weiterhin allein der Server (ADR-0052 Ziffer 5). Was er
 * NICHT überfällig nennt und dennoch nicht nach dem Berliner Heute liegt, ist
 * heute fällig -- der Browser trennt nur "heute" von "später", er erklärt
 * nichts für überfällig. `jetzt` kommt herein, damit die Probe keine Uhr braucht.
 */
export function groupByDue<T extends Pick<TaskView, 'dueOn' | 'overdue'>>(
  tasks: readonly T[],
  jetzt: Date,
): ReadonlyArray<{ readonly gruppe: FaelligkeitsGruppe; readonly eintraege: readonly T[] }> {
  const heute = todayInBerlin(jetzt);
  const sonntag = wochenendeVon(heute);
  const gruppen: Record<FaelligkeitsGruppe, T[]> = { ueberfaellig: [], heute: [], 'diese-woche': [], spaeter: [] };
  for (const task of tasks) {
    gruppen[task.overdue ? 'ueberfaellig' : task.dueOn <= heute ? 'heute' : task.dueOn <= sonntag ? 'diese-woche' : 'spaeter'].push(task);
  }
  return (['ueberfaellig', 'heute', 'diese-woche', 'spaeter'] as const)
    .filter((g) => gruppen[g].length > 0)
    .map((gruppe) => ({ gruppe, eintraege: gruppen[gruppe] }));
}

/**
 * Der Sonntag der Kalenderwoche (Montag bis Sonntag) zu einem Berliner Tag YYYY-MM-DD. Gerechnet
 * wird auf dem Datum, nicht auf einer Uhrzeit: der Tag kommt schon in der Produktzeitzone herein.
 */
export function wochenendeVon(tag: string): string {
  const [jahr, monat, t] = tag.split('-').map(Number) as [number, number, number];
  const datum = new Date(Date.UTC(jahr, monat - 1, t));
  const bisSonntag = (7 - datum.getUTCDay()) % 7;
  return new Date(Date.UTC(jahr, monat - 1, t + bisSonntag)).toISOString().slice(0, 10);
}

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return { status: response.status, answered: payload !== undefined, ...correlationOf(body) };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

/** Der erste Feldfehler der Antwort -- in Worten, ohne Feldnamen und Grundcode. */
function fieldMessageOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  const errors = Array.isArray(body.errors) ? body.errors : [];
  const first = (errors[0] ?? {}) as Record<string, unknown>;
  if (first.fieldPath !== 'dueOn') return undefined;
  return first.reasonCode === 'field_value_unsupported'
    ? 'Wähl heute oder einen späteren Tag.'
    : 'Für welchen Tag möchtest du dir das vormerken?';
}

function viewOf(value: unknown): TaskView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.taskId !== 'string' ||
    typeof row.title !== 'string' ||
    typeof row.dueOn !== 'string' ||
    typeof row.overdue !== 'boolean' ||
    typeof row.status !== 'string' ||
    typeof row.lockVersion !== 'number' ||
    typeof row.partyId !== 'string' ||
    typeof row.partyDisplayName !== 'string' ||
    typeof row.interactionId !== 'string' ||
    typeof row.createdAt !== 'string'
  ) {
    return undefined;
  }
  return {
    taskId: row.taskId,
    title: row.title,
    dueOn: row.dueOn,
    overdue: row.overdue,
    status: row.status,
    lockVersion: row.lockVersion,
    partyId: row.partyId,
    partyDisplayName: row.partyDisplayName,
    interactionId: row.interactionId,
    createdAt: row.createdAt,
  };
}

const UNVOLLSTAENDIG: PlainMessage = Object.freeze({
  headline: 'Deine Wiedervorlagen lassen sich nicht anzeigen.',
  explanation: 'LIAISON hat geantwortet, aber die Antwort war unvollständig.',
  nextStep: 'Lade die Seite neu. Bleibt es dabei, wende dich an die LIAISON-Administration.',
  retryable: true,
});

export interface ListOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly cursor?: string;
  readonly send?: SendLike;
}

export async function fetchOwnTasks(options: ListOptions): Promise<TasksResult> {
  const send = options.send ?? fetch;
  const query = options.cursor === undefined ? '' : `?cursor=${encodeURIComponent(options.cursor)}`;
  const response = await send(`${options.baseUrl}/tasks${query}`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(body.tasks) ? body.tasks : [];
  const tasks: TaskView[] = [];
  for (const row of rows) {
    const view = viewOf(row);
    if (view === undefined) {
      return { outcome: 'rejected', message: UNVOLLSTAENDIG };
    }
    tasks.push(view);
  }
  return {
    outcome: 'loaded',
    tasks,
    hasMore: body.hasMore === true,
    ...(typeof body.nextCursor === 'string' ? { nextCursor: body.nextCursor } : {}),
  };
}

/**
 * Die gemeinsame Auswertung aller Befehle; `eigene` gilt vor den gemeinsamen Saetzen. 404 ist hier KEIN eigener
 * Ausgang mit eigenem Text: "gibt es nicht" und "darfst du nicht sehen"
 * antworten gleich, und die Oberflaeche darf den Unterschied nicht erfinden.
 */
async function commandResult(response: Response, eigene?: Readonly<Record<string, PlainMessage>>): Promise<TaskCommandResult> {
  const payload = await response.json().catch(() => undefined);
  if (response.status === 404) {
    return { outcome: 'gone' };
  }
  if (response.status === 422) {
    const message = fieldMessageOf(payload);
    if (message !== undefined) return { outcome: 'field-rejected', message };
  }
  if (!response.ok) {
    const grund = reasonCodeOf(payload);
    const satz = grund === undefined ? undefined : (eigene?.[grund] ?? REASON_MESSAGES[grund]);
    const message = satz ?? plainMessageFor(grund, factsOf(response, payload));
    return grund === undefined ? { outcome: 'rejected', message } : { outcome: 'rejected', message, reasonCode: grund };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  if (
    typeof body.taskId !== 'string' ||
    typeof body.dueOn !== 'string' ||
    typeof body.lockVersion !== 'number' ||
    typeof body.status !== 'string'
  ) {
    return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  }
  return { outcome: 'applied', taskId: body.taskId, dueOn: body.dueOn, lockVersion: body.lockVersion, status: body.status };
}

export interface CreateOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly interactionId: string;
  readonly dueOn: string;
  /** Leer heisst: LIAISON traegt die Vorbelegung ein. */
  readonly title?: string;
  readonly send?: SendLike;
}

export async function createFollowUp(options: CreateOptions): Promise<TaskCommandResult> {
  const send = options.send ?? fetch;
  const titel = (options.title ?? '').trim();
  const response = await send(
    `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/interactions/${encodeURIComponent(options.interactionId)}/tasks`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
      body: JSON.stringify({ dueOn: options.dueOn, ...(titel.length === 0 ? {} : { title: titel }) }),
    },
  );
  return commandResult(response, CREATE_MESSAGES);
}

export interface ChangeOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly taskId: string;
  readonly lockVersion: number;
  readonly send?: SendLike;
}

async function command(
  options: ChangeOptions,
  pfad: string,
  rumpf: Record<string, unknown> = {},
  eigene?: Readonly<Record<string, PlainMessage>>,
): Promise<TaskCommandResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/tasks/${encodeURIComponent(options.taskId)}/${pfad}`, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({ lockVersion: options.lockVersion, ...rumpf }),
  });
  return commandResult(response, eigene);
}

export function completeFollowUp(options: ChangeOptions): Promise<TaskCommandResult> {
  return command(options, 'complete');
}

export function cancelFollowUp(options: ChangeOptions): Promise<TaskCommandResult> {
  return command(options, 'cancel');
}

export function changeFollowUpDue(options: ChangeOptions & { readonly dueOn: string }): Promise<TaskCommandResult> {
  return command(options, 'due-date', { dueOn: options.dueOn }, DUE_CHANGE_MESSAGES);
}

/** Nur unmittelbar nach "Erledigt": `taskId` und `lockVersion` kommen aus dessen Antwort. */
export function reopenFollowUp(options: ChangeOptions): Promise<TaskCommandResult> {
  return command(options, 'reopen', {}, REOPEN_MESSAGES);
}

/** Der heutige Kalendertag in Berliner Zeit -- fuer die Vorbelegung und die Feldregel. */
export function todayInBerlin(now: Date = new Date()): string {
  // `sv-SE` liefert YYYY-MM-DD; die Zeitzone macht daraus den Berliner Tag.
  return new Intl.DateTimeFormat('sv-SE', { timeZone: PRODUKT_ZEITZONE }).format(now);
}

/**
 * Die Faelligkeiten der Eingabe "Wiedervorlage" (Slice 5a). "anderer" oeffnet
 * ein Datumsfeld; "keine" ist die Vorbelegung.
 */
export const FAELLIGKEITEN = [
  { id: 'keine', label: 'keine' },
  { id: 'morgen', label: 'morgen' },
  { id: 'woche', label: 'in einer Woche' },
  { id: 'zwei-wochen', label: 'in zwei Wochen' },
  { id: 'monat', label: 'in einem Monat' },
  { id: 'anderer', label: 'anderer Tag …' },
] as const;

export type FaelligkeitId = (typeof FAELLIGKEITEN)[number]['id'];

/**
 * Der Tag (YYYY-MM-DD) zu einer Option, ausgehend von `heute` (YYYY-MM-DD, in
 * der Praxis todayInBerlin). Wochenenden werden NICHT verschoben: Kalenderlogik
 * ohne Anforderung. "In einem Monat" ist der gleiche Tag im Folgemonat, sonst
 * dessen letzter Tag (31.01. -> 28./29.02.). "keine" und "anderer" haben keinen
 * Tag aus der Option.
 */
export function faelligkeitFuer(option: FaelligkeitId, heute: string): string | undefined {
  const [jahr, monat, tag] = heute.split('-').map(Number) as [number, number, number];
  const zuText = (datum: Date): string => datum.toISOString().slice(0, 10);
  switch (option) {
    case 'morgen':
      return zuText(new Date(Date.UTC(jahr, monat - 1, tag + 1)));
    case 'woche':
      return zuText(new Date(Date.UTC(jahr, monat - 1, tag + 7)));
    case 'zwei-wochen':
      return zuText(new Date(Date.UTC(jahr, monat - 1, tag + 14)));
    case 'monat': {
      const letzter = new Date(Date.UTC(jahr, monat + 1, 0)).getUTCDate();
      return zuText(new Date(Date.UTC(jahr, monat, Math.min(tag, letzter))));
    }
    default:
      return undefined;
  }
}
